import { useCallback, useMemo, useState } from "react";

interface IEntry<T> {
  id: number;
  value: T;
}

export interface IUseMultiItemNavigationReturn<T> {
  items: T[];
  currentItem: T | undefined;
  /** Identité stable de l'item courant, à utiliser comme `key` React pour forcer le remount du formulaire. */
  currentKey: number | undefined;
  currentIndex: number;
  totalCount: number;
  isFirst: boolean;
  isLast: boolean;
  goNext: () => void;
  goPrevious: () => void;
  deleteCurrentItem: () => void;
  updateCurrentItem: (value: T) => void;
  keepItemsAt: (indexes: number[]) => void;
}

const toEntries = <T>(values: T[]): IEntry<T>[] => values.map((value, id) => ({ id, value }));

/**
 * Navigation dans une liste d'items éditables en mémoire.
 * Le hook est la seule source de vérité : les éditions sont persistées via `updateCurrentItem`
 * et la liste complète est exposée via `items` pour la soumission finale.
 *
 * `initialItems` doit avoir une référence stable (typiquement la réponse de l'orchestration IA) :
 * un changement de référence réinitialise la session.
 */
export const useMultiItemNavigation = <T>(initialItems: T[]): IUseMultiItemNavigationReturn<T> => {
  const [entries, setEntries] = useState<IEntry<T>[]>(() => toEntries(initialItems));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevInitialItems, setPrevInitialItems] = useState(initialItems);

  if (initialItems !== prevInitialItems) {
    setPrevInitialItems(initialItems);
    setEntries(toEntries(initialItems));
    setCurrentIndex(0);
  }

  const goNext = useCallback(() => setCurrentIndex((i) => i + 1), []);
  const goPrevious = useCallback(() => setCurrentIndex((i) => i - 1), []);

  const updateCurrentItem = useCallback(
    (value: T) => {
      setEntries((prev) => prev.map((entry, i) => (i === currentIndex ? { ...entry, value } : entry)));
    },
    [currentIndex]
  );

  const deleteCurrentItem = useCallback(() => {
    const nextLength = entries.length - 1;
    setEntries((prev) => prev.filter((_, i) => i !== currentIndex));
    // Reste sur l'item suivant (qui prend l'index courant) ; recule si on supprimait le dernier.
    setCurrentIndex((i) => Math.max(0, Math.min(i, nextLength - 1)));
  }, [currentIndex, entries.length]);

  const keepItemsAt = useCallback((indexes: number[]) => {
    const toKeep = new Set(indexes);
    setEntries((prev) => prev.filter((_, i) => toKeep.has(i)));
    setCurrentIndex(0);
  }, []);

  const items = useMemo(() => entries.map((entry) => entry.value), [entries]);
  const current = entries[currentIndex];
  const totalCount = entries.length;

  return {
    items,
    currentItem: current?.value,
    currentKey: current?.id,
    currentIndex,
    totalCount,
    isFirst: currentIndex === 0,
    isLast: currentIndex === totalCount - 1,
    goNext,
    goPrevious,
    deleteCurrentItem,
    updateCurrentItem,
    keepItemsAt,
  };
};
